import React, { forwardRef } from "react";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { amountInWords } from "src/utils/numberToWords";
import { formatStateWithGstCode } from "src/constants/indianStates";
import { getPanFromGst } from "src/utils/gst";
import { formatAddress } from "src/utils/formatAddress";
import { ORDER_PAYMENT_STATUS } from "src/components/payments/paymentLabels";

interface InvoiceTenant {
  name?: string;
  gst?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  email?: string;
}

interface InvoiceItem {
  productId?: { name?: string; hsnCode?: string; sku?: string };
  hsnCode?: string;
  sku?: string;
  quantity: number;
  sellingPrice: number;
  discountPercent?: number;
  taxPercent?: number;
  taxAmount?: number;
  totalPrice: number;
}

interface InvoiceOrder {
  orderId: string;
  createdAt: string;
  completedAt?: string;
  paymentMode?: string;
  paymentStatus?: string;
  amountPaid?: number;
  balanceDue?: number;
  subTotal: number;
  taxAmount: number;
  discount?: number;
  grandTotal: number;
  customerId?: {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    pincode?: string;
    mobile?: string;
    gstNumber?: string;
    state?: string;
  };
  items?: InvoiceItem[];
}

interface InvoiceDocumentProps {
  order: InvoiceOrder;
  tenant?: InvoiceTenant | null;
  className?: string;
}

/** The literal print-ready A4 tax invoice layout — shared by the "Generate
 *  Invoice" preview modal, the PDF export, and the public invoice link, so
 *  all three always render identically. */
const InvoiceDocument = forwardRef<HTMLDivElement, InvoiceDocumentProps>(
  ({ order, tenant, className = "" }, ref) => {
    const items: InvoiceItem[] = order.items || [];
    const totalQty = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const discount = Number(order.discount || 0);
    const tenantPan = getPanFromGst(tenant?.gst);

    const hsnSummary = items.reduce((acc, item) => {
      const hsn = item.hsnCode || item.productId?.hsnCode || "-";
      const taxable = Number(item.totalPrice || 0);
      const tax = Number(item.taxAmount || 0);
      if (!acc[hsn]) acc[hsn] = { taxable: 0, tax: 0 };
      acc[hsn].taxable += taxable;
      acc[hsn].tax += tax;
      return acc;
    }, {} as Record<string, { taxable: number; tax: number }>);

    return (
      <div
        ref={ref}
        className={`pdf-container bg-white border border-gray-800 text-gray-900 text-sm ${className}`}
      >
        {/* Company header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-800">
          <div>
            <h1 className="text-xl font-bold uppercase text-gray-900">
              {tenant?.name || "Your Business Name"}
            </h1>
            <div className="mt-1 text-xs text-gray-600 space-y-0.5">
              {formatAddress(tenant) && <p>{formatAddress(tenant)}</p>}
              <p>
                {tenant?.gst && <>GSTIN: {tenant.gst} &nbsp;·&nbsp; </>}
                {tenantPan && <>PAN: {tenantPan} &nbsp;·&nbsp; </>}
                {tenant?.email && <>Email: {tenant.email}</>}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <h2 className="text-2xl font-bold text-gray-900">TAX INVOICE</h2>
            <p className="text-xs text-gray-500 mt-1">
              ORIGINAL FOR RECIPIENT
            </p>
          </div>
        </div>

        {/* Customer detail + Invoice detail */}
        <div className="grid grid-cols-2 border-b border-gray-800">
          <div className="p-4 border-r border-gray-800 space-y-1">
            <p className="font-semibold text-gray-700 mb-2">
              Customer Detail
            </p>
            <p>
              <span className="font-medium">M/S: </span>
              {order.customerId?.name}
            </p>
            <p>
              <span className="font-medium">Address: </span>
              {formatAddress(order.customerId) || "-"}
            </p>
            <p>
              <span className="font-medium">Phone: </span>
              {order.customerId?.mobile || "-"}
            </p>
            <p>
              <span className="font-medium">GSTIN: </span>
              {order.customerId?.gstNumber || "-"}
            </p>
            <p>
              <span className="font-medium">Place of Supply: </span>
              {formatStateWithGstCode(order.customerId?.state)}
            </p>
          </div>

          <div className="p-4 space-y-1">
            <div className="grid grid-cols-2">
              <span className="font-medium">Invoice No.</span>
              <span className="font-semibold">{order.orderId}</span>
            </div>
            <div className="grid grid-cols-2">
              <span className="font-medium">Invoice Date</span>
              <span>
                {formatDateTime(
                  order.completedAt || order.createdAt,
                  "custom",
                  "DD-MMM-YYYY"
                )}
              </span>
            </div>
            <div className="grid grid-cols-2">
              <span className="font-medium">Payment Mode</span>
              <span>{order.paymentMode?.toUpperCase() || "-"}</span>
            </div>
            <div className="grid grid-cols-2">
              <span className="font-medium">Payment Status</span>
              <span>
                {ORDER_PAYMENT_STATUS[order.paymentStatus || ""]?.text || "-"}
              </span>
            </div>
            {(order.amountPaid ?? 0) > 0 && (order.balanceDue ?? 0) > 0 && (
              <>
                <div className="grid grid-cols-2">
                  <span className="font-medium">Amount Paid</span>
                  <span>{currencyFormatter(order.amountPaid || 0)}</span>
                </div>
                <div className="grid grid-cols-2">
                  <span className="font-medium">Balance Due</span>
                  <span>{currencyFormatter(order.balanceDue || 0)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Line items */}
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-800 text-xs uppercase">
              <th className="p-2 border-r border-gray-800 w-10 text-left">
                Sr.
              </th>
              <th className="p-2 border-r border-gray-800 text-left">
                Name of Product / Service
              </th>
              <th className="p-2 border-r border-gray-800 w-20 text-center">
                SKU
              </th>
              <th className="p-2 border-r border-gray-800 w-20 text-center">
                HSN/SAC
              </th>
              <th className="p-2 border-r border-gray-800 w-16 text-right">
                Qty
              </th>
              <th className="p-2 border-r border-gray-800 w-24 text-right">
                Rate
              </th>
              <th className="p-2 w-28 text-right">Taxable Value</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-200">
                <td className="p-2 border-r border-gray-200">{idx + 1}</td>
                <td className="p-2 border-r border-gray-200 font-medium">
                  {item.productId?.name}
                </td>
                <td className="p-2 border-r border-gray-200 text-center">
                  {item.sku || item.productId?.sku || "-"}
                </td>
                <td className="p-2 border-r border-gray-200 text-center">
                  {item.hsnCode || item.productId?.hsnCode || "-"}
                </td>
                <td className="p-2 border-r border-gray-200 text-right">
                  {item.quantity}
                </td>
                <td className="p-2 border-r border-gray-200 text-right">
                  {currencyFormatter(item.sellingPrice.toFixed(2))}
                </td>
                <td className="p-2 text-right">
                  {currencyFormatter(item.totalPrice.toFixed(2))}
                </td>
              </tr>
            ))}

            {/* Filler row so the summary below sits at a consistent sheet
                height even for short orders */}
            <tr>
              <td colSpan={7} className="p-2 min-h-[80px] print:min-h-0">
                &nbsp;
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-800">
              <td
                colSpan={4}
                className="p-2 border-r border-gray-800 font-semibold"
              >
                Total
              </td>
              <td className="p-2 border-r border-gray-800 text-right font-semibold">
                {totalQty}
              </td>
              <td className="p-2 border-r border-gray-800" />
              <td className="p-2 text-right font-semibold">
                {currencyFormatter(order.grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Amount summary */}
        <div className="flex justify-end border-b border-gray-800 p-4">
          <table className="text-sm w-64">
            <tbody>
              <tr>
                <td className="py-0.5">Subtotal</td>
                <td className="py-0.5 text-right">
                  {currencyFormatter(order.subTotal)}
                </td>
              </tr>
              <tr>
                <td className="py-0.5">GST</td>
                <td className="py-0.5 text-right">
                  {currencyFormatter(order.taxAmount)}
                </td>
              </tr>
              {discount > 0 && (
                <tr>
                  <td className="py-0.5">Less: Discount</td>
                  <td className="py-0.5 text-right">
                    -{currencyFormatter(discount)}
                  </td>
                </tr>
              )}
              <tr className="border-t border-gray-300 font-semibold">
                <td className="py-1">Grand Total</td>
                <td className="py-1 text-right">
                  {currencyFormatter(order.grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Total in words */}
        <div className="px-4 py-3 border-b border-gray-800">
          <p className="text-xs text-gray-500">Total in words</p>
          <p className="font-semibold uppercase">
            {amountInWords(Number(order.grandTotal || 0))}
          </p>
        </div>

        {/* HSN-wise tax summary */}
        <table className="w-full border-collapse border-b border-gray-800">
          <thead>
            <tr className="border-b border-gray-800 text-xs uppercase">
              <th className="p-2 border-r border-gray-800 text-left">
                HSN / SAC
              </th>
              <th className="p-2 border-r border-gray-800 text-right">
                Taxable Value
              </th>
              <th className="p-2 border-r border-gray-800 text-right w-20">
                GST %
              </th>
              <th className="p-2 border-r border-gray-800 text-right">
                GST Amount
              </th>
              <th className="p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(hsnSummary).map(([hsn, row]) => {
              const ratePercent =
                row.taxable > 0 ? (row.tax / row.taxable) * 100 : 0;
              return (
                <tr key={hsn} className="border-b border-gray-200">
                  <td className="p-2 border-r border-gray-200">{hsn}</td>
                  <td className="p-2 border-r border-gray-200 text-right">
                    {currencyFormatter(row.taxable.toFixed(2))}
                  </td>
                  <td className="p-2 border-r border-gray-200 text-right">
                    {ratePercent.toFixed(2)}%
                  </td>
                  <td className="p-2 border-r border-gray-200 text-right">
                    {currencyFormatter(row.tax.toFixed(2))}
                  </td>
                  <td className="p-2 text-right">
                    {currencyFormatter((row.taxable + row.tax).toFixed(2))}
                  </td>
                </tr>
              );
            })}
            <tr className="font-semibold">
              <td className="p-2 border-r border-gray-200">Total</td>
              <td className="p-2 border-r border-gray-200 text-right">
                {currencyFormatter(order.subTotal)}
              </td>
              <td className="p-2 border-r border-gray-200" />
              <td className="p-2 border-r border-gray-200 text-right">
                {currencyFormatter(order.taxAmount)}
              </td>
              <td className="p-2 text-right">
                {currencyFormatter(
                  Number(order.subTotal || 0) + Number(order.taxAmount || 0)
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="px-4 py-3 border-b border-gray-800">
          <p className="text-xs text-gray-500">Total tax in words</p>
          <p className="font-semibold uppercase">
            {amountInWords(Number(order.taxAmount || 0))}
          </p>
        </div>

        {/* Bank details + certification */}
        <div className="grid grid-cols-2 border-b border-gray-800">
          <div className="p-4 border-r border-gray-800">
            <p className="font-semibold text-gray-700 mb-2">Bank Details</p>
            <p className="text-xs text-gray-400 italic">
              Not set up yet — bank details will appear here once added.
            </p>
          </div>
          <div className="p-4 text-center">
            <p className="text-xs text-gray-500">
              Certified that the particulars given above are true and
              correct.
            </p>
            <p className="font-semibold mt-1">For {tenant?.name || "-"}</p>
            <p className="mt-10 print:mt-4 text-xs text-gray-500">
              Authorised Signatory
            </p>
          </div>
        </div>

        {/* Terms + customer signature */}
        <div className="grid grid-cols-2">
          <div className="p-4 border-r border-gray-800">
            <p className="font-semibold text-gray-700 mb-2">
              Terms and Conditions
            </p>
            <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
              <li>Goods once sold will not be taken back.</li>
              <li>Interest will be charged on overdue payments.</li>
              <li>Subject to local jurisdiction.</li>
            </ul>
          </div>
          <div className="p-4 flex flex-col justify-end">
            <p className="text-xs text-gray-500 border-t border-gray-300 pt-1 text-center">
              Customer Signature
            </p>
          </div>
        </div>
      </div>
    );
  }
);

InvoiceDocument.displayName = "InvoiceDocument";

export default InvoiceDocument;
